/**
  * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * WelcomeCard
 * Main component to show welcome card with all sections
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import mixins from '../../assets/styles/mixins.scss';
import CustomBanner from './CustomBanner';

const useWelcomeCardStyles = makeStyles({
  root: {
    width: '100%',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 32,
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: mixins.fontSize24,
    fontWeight: mixins.fontWeight700,
    color: mixins.color_text_primary,
    marginBottom: 16,
    lineHeight: '120%'
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    color: mixins.color_text_primary,
    lineHeight: '28px',
    fontFamily: mixins.fontFamilyPlusSans,
    letterSpacing: '-0.4px'
  },
  instructionsList: {
    paddingLeft: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  instructionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: mixins.fontSize18,
    color: mixins.color_text_primary,
    lineHeight: '28px',
    gap: 8,
    fontFamily: mixins.fontFamilyPlusSans
  },
  instructionNumber: {
    fontFamily: mixins.fontFamilyPlusSans,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize18
  },
  instructionText: {
    fontFamily: mixins.fontFamilyPlusSans,
    flex: 1,
    fontSize: mixins.fontSize18,
    color: mixins.color_text_primary,
    lineHeight: '28px'
  },
  instructionHeader: {
    fontFamily: mixins.fontFamilyPlusSans,
    display: 'flex',
    alignItems: 'baseline'
  },
  instructionContent: {
    fontFamily: mixins.fontFamilyPlusSans,
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  instructionTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    color: mixins.color_text_primary,
    lineHeight: '28px'
  },
  instructionDescription: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    color: mixins.zambezi,
    lineHeight: '24px'
  },
  ctaButton: {
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '12px 24px',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    textTransform: 'none',
    borderRadius: 1000,
    minWidth: 140,
    width: 168,
    height: 52,
    position: 'relative',
    '&:hover': {
      backgroundColor: mixins.amethyst,
      opacity: 0.9,
      color: mixins.chalkWhiteFont
    },
    '& .MuiButton-label': {
      width: '100%',
      gap: '8px',
      display: 'inherit'
    }
  },
  ctaButtonLine: {
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 2,
    height: 20,
    backgroundColor: mixins.amethyst
  },
  illustrationContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    '& img': {
      width: 300
    }
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  ctaButtonContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'column'
  }
});



/**
 * WelcomeCard Component
 * Main component that renders the welcome card with all sections
 */
const WelcomeCard = ({
  image,
  header,
  content,
  buttonCta,
  showButton,
  onCtaClick,
  className,
  banner,
  showBanner,
  onInit = () => null
}) => {
  const classes = useWelcomeCardStyles();
  const [isBannerVisible, setIsBannerVisible] = useState(showBanner);

  const handleBannerClose = () => {
    setIsBannerVisible(false);
    banner.onBannerClose();
  };


  useEffect(() => {
    onInit();
  }, []);

  return (
    <div className={`${classes.root} ${className || ''}`}>
      {header && (
        <Typography className={classes.title}>
          {header}
        </Typography>
      )}
      {isBannerVisible && (
        <CustomBanner
          type={banner.type}
          text={banner.text}
          onClose={handleBannerClose}
        />
      )}
      {content && (
        <div className={classes.contentContainer}>
          <Typography className={classes.sectionTitle} variant="h5">
            {content.sectionTitle}
          </Typography>

          {content.instructions && (
            <div className={classes.instructionsList}>
              {content.instructions.map((instruction, index) => (
                <div key={index} className={classes.instructionItem}>
                  <div className={classes.instructionHeader}>
                    <div className={classes.instructionContent}>
                      {typeof instruction === 'string' ? (
                        <Typography className={classes.instructionText}>
                          {instruction}
                        </Typography>
                      ) : (
                        <>
                          <Typography className={classes.instructionTitle}>
                            {instruction.title}
                          </Typography>
                          <Typography className={classes.instructionDescription}>
                            {instruction.description}
                          </Typography>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showButton && buttonCta && (
        <div className={classes.ctaButtonContainer}>
          <Button
            className={classes.ctaButton}
            onClick={onCtaClick}
            variant="contained"
          >
            {buttonCta.text}
            {buttonCta.icon}
          </Button>
        </div>
      )}
      {image && (
        <div className={classes.illustrationContainer}>
          {image}
        </div>
      )}
    </div>
  );
};

WelcomeCard.propTypes = {
  image: PropTypes.node,
  header: PropTypes.string,
  content: PropTypes.shape({
    sectionTitle: PropTypes.string,
    instructions: PropTypes.arrayOf(PropTypes.string)
  }),
  buttonCta: PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.node
  }),
  showButton: PropTypes.bool,
  onCtaClick: PropTypes.func,
  className: PropTypes.string,
  banner: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
    onBannerClose: PropTypes.func
  }),
  showBanner: PropTypes.bool,
  onInit: PropTypes.func
};

WelcomeCard.defaultProps = {
  showButton: true,
  content: {
    sectionTitle: '',
    instructions: [
    ]
  },
  buttonCta: {
    text: '',
    icon: null
  },
  onCtaClick: () => {},
  className: '',
  image: null,
  header: null,
  banner: {
    type: 'alert',
    text: '',
    onBannerClose: () => {}
  },
  showBanner: false,
  onInit: () => {}
};

export default WelcomeCard;
